import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { buildCityService, buildBreadcrumb } from '../../../../lib/structuredData';
import Header from '../../../../components/ui/Header';
import Footer from '../../../../components/Footer';
import InstantEstimateHero from '../../../homepage/components/InstantEstimateHero';
import { openCustomerPortal } from '../../../../utils/cn';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN;

const AddressInputWithMapbox = ({ placeholder, buttonText, buttonId, microcopy }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (val) => {
    if (!val || val?.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(val)}.json?access_token=${MAPBOX_TOKEN}&types=address&country=US&proximity=-84.1916,39.7589&autoComplete=true`
      );
      const data = await res?.json();
      setSuggestions(data?.features || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e?.target?.value;
    setQuery(val);
    clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (place) => {
    setQuery(place?.place_name || '');
    setSuggestions([]);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSuggestions([]);
    // Carry the typed address into the portal so the wizard opens pre-filled.
    openCustomerPortal(query);
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="relative flex-1">
          {/* Location pin icon inside input */}
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <svg className="w-4 h-4" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={query}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full pl-10 pr-5 py-4 rounded-full text-gray-800 text-sm outline-none border border-gray-200 focus:border-blue-400 bg-white"
            style={{ fontFamily: "'Inter', sans-serif", boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
            autoComplete="off"
          />
          {suggestions?.length > 0 && (
            <div
              className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl overflow-hidden z-50"
              style={{ boxShadow: '0 8px 32px rgba(0,1,80,0.14)', border: '1px solid rgba(7,176,233,0.15)' }}
            >
              {suggestions?.map((s) => (
                <button
                  key={s?.id}
                  type="button"
                  onClick={() => handleSelect(s)}
                  className="w-full text-left px-5 py-3 text-sm text-gray-700 hover:bg-blue-50 transition-colors duration-150 border-b border-gray-50 last:border-0"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <span className="font-medium text-gray-900">{s?.text}</span>
                  <span className="text-gray-400 ml-1">{s?.place_name?.replace(s?.text, '')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="w-full px-8 rounded-full text-white font-bold text-sm whitespace-nowrap transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
          style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 20px rgba(7,176,233,0.35)', fontFamily: "'Poppins', 'Inter', sans-serif", minHeight: '52px' }}
        >
          {loading ? 'Loading...' : buttonText}
        </button>
      </form>
      {microcopy && (
        <p className="text-center text-xs mt-3" style={{ color: 'rgba(255,255,255,0.65)', fontFamily: "'Inter', sans-serif" }}>
          {microcopy}
        </p>
      )}
      {/* Hidden Demand IQ trigger */}
      <button id="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea" className="hidden" aria-hidden="true" />
    </div>
  );
};

const DaytonServiceAreaPage = () => {
  const bgRef = useRef(null);
  const cardRef = useRef(null);
  const [cardVisible, setCardVisible] = useState(false);
  const [ticksVisible, setTicksVisible] = useState([false, false, false]);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Parallax effect — disabled on mobile for performance
  useEffect(() => {
    if (isMobile) {
      if (bgRef?.current) bgRef.current.style.transform = 'none';
      return;
    }
    const handleScroll = () => {
      if (bgRef?.current) {
        const scrollY = window.scrollY;
        bgRef.current.style.transform = `translateY(${scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Fade-in slide-up animation for card on mount
  useEffect(() => {
    const timer = setTimeout(() => setCardVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Staggered animated tick icons
  useEffect(() => {
    if (!cardVisible) return;
    [0, 1, 2]?.forEach((i) => {
      setTimeout(() => {
        setTicksVisible((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 600 + i * 180);
    });
  }, [cardVisible]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'Poppins', 'Inter', sans-serif" }}>
      <Helmet>
        <title>Top Rated Dayton Roofing Companies | Exact Online Roof Prices</title>
        <meta
          name="description"
          content="OpenbooQ serves the Dayton, Ohio area with transparent roof replacements, roof prices, and trusted local installers. Get your free quote today."
        />
        <meta name="keywords" content="dayton roofing companies, roofing companies dayton, dayton ohio roofing, roof replacement dayton, instant roof estimate dayton" />
        <script type="application/ld+json">
          {JSON.stringify(buildCityService({ name: 'Dayton', region: 'OH', url: 'https://openbooq.com/service-areas/ohio/dayton' }))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumb([
            { name: 'Home', item: 'https://openbooq.com' },
            { name: 'Service Areas', item: 'https://openbooq.com/service-areas' },
            { name: 'Dayton, OH', item: 'https://openbooq.com/service-areas/ohio/dayton' },
          ]))}
        </script>
      </Helmet>
      <Header />
      {/* ===== HERO SECTION ===== */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ backgroundColor: '#000150', paddingTop: '80px' }}
      >
        {/* Background image with parallax - full bleed */}
        <div
          ref={bgRef}
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/assets/images/8820823NL0213-1773087799641.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            top: '-10%',
            bottom: '-10%',
            willChange: isMobile ? 'auto' : 'transform',
          }}
        />
        {/* Full-bleed dark gradient overlay — bottom-heavy for text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, rgba(0,1,80,0.82) 0%, rgba(0,1,80,0.65) 40%, rgba(0,1,80,0.45) 70%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        {/* Subtle grid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#07B0E9" strokeWidth="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-grid)" />
          </svg>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left: Headline + Trust Badges */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                Dayton, Ohio
              </div>

              {/* Larger, bolder headline with brand blue gradient highlight */}
              <h1
                className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.03em' }}
              >
                Roofing
                <br />
                <span
                  style={{
                    background: 'linear-gradient(90deg, #07B0E9 0%, #0ea5e9 50%, #38bdf8 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Reimagined
                </span>
                <br />
                <span className="text-white">for Dayton.</span>
              </h1>

              <p className="text-gray-300 text-base sm:text-xl mb-8 leading-relaxed max-w-xl">
                Same materials. Better warranty. Thousands less. Skip the pushy sales reps and get an accurate, AI-powered roof replacement price in minutes.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-8 sm:mb-10">
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Energy Star Certified</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">Impact Resistant</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full"
                  style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                >
                  <svg className="w-5 h-5" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-white text-xs font-bold uppercase tracking-wider">30-Minute Roof Price</span>
                </div>
              </div>
            </div>

            {/* Right: Glassmorphism Card with fade-in slide-up animation */}
            <div className="flex justify-center lg:justify-end">
              <div
                ref={cardRef}
                className="w-full max-w-md rounded-3xl p-5 sm:p-8"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 24px 64px rgba(0,1,80,0.35), 0 4px 16px rgba(7,176,233,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
                  opacity: cardVisible ? 1 : 0,
                  transform: cardVisible ? 'translateY(0)' : 'translateY(32px)',
                  transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
                }}
              >
                <div className="text-center mb-5 sm:mb-6">
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3"
                    style={{ backgroundColor: 'rgba(7,176,233,0.2)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.35)' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    Free Roof Price
                  </div>
                  <h2
                    className="text-2xl sm:text-3xl font-black mb-2"
                    style={{ color: '#ffffff', fontFamily: "'Poppins', sans-serif", textShadow: '0 1px 4px rgba(0,0,0,0.2)', letterSpacing: '-0.02em', lineHeight: 1.15 }}
                  >
                    Get My{' '}
                    <span
                      style={{
                        background: 'linear-gradient(90deg, #07B0E9 0%, #38bdf8 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      Roof Price
                    </span>
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', letterSpacing: '0.02em' }}>Enter your Dayton address below</p>
                </div>

                <div className="space-y-3">
                  <AddressInputWithMapbox
                    placeholder="Enter your Dayton home address..."
                    buttonText="Get My Roof Price"
                    buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea"
                    microcopy={null}
                  />
                </div>

                <p
                  className="text-center text-xs mt-4"
                  style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Inter', sans-serif" }}
                >
                  Free roof price • No sales calls • Dayton & Miami Valley
                </p>

                {/* Trust row with animated tick icons */}
                <div className="flex items-center justify-center gap-3 sm:gap-4 mt-5 pt-5 flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                  {[
                    { label: 'No credit card' },
                    { label: 'No sales calls' },
                    { label: '30 minutes' },
                  ]?.map((item, i) => (
                    <div key={item?.label} className="flex items-center gap-1.5">
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: ticksVisible?.[i] ? '#22C55E' : 'transparent',
                          border: ticksVisible?.[i] ? '2px solid #22C55E' : '2px solid rgba(255,255,255,0.3)',
                          transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
                          transform: ticksVisible?.[i] ? 'scale(1)' : 'scale(0.6)',
                          opacity: ticksVisible?.[i] ? 1 : 0.4,
                        }}
                      >
                        <svg
                          style={{
                            width: '10px',
                            height: '10px',
                            color: '#fff',
                            opacity: ticksVisible?.[i] ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            transitionDelay: ticksVisible?.[i] ? '0.1s' : '0s',
                          }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>{item?.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ===== INSTANT ESTIMATE HERO SECTION ===== */}
      <InstantEstimateHero />
      {/* ===== BETTER WAY SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
              >
                A Better Way
              </div>
              <h2
                className="text-3xl sm:text-4xl font-black mb-6 leading-tight"
                style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
              >
                The Modern Alternative to Traditional Dayton Roofing Companies
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-5">
                Let's face it: getting a new roof usually means taking time off work, waiting for contractors to show up, and sitting through high-pressure sales pitches. We built OpenbooQ to fix that. As the most innovative among dayton roofing companies, we allow you to design, purchase, and schedule your roof entirely from the comfort of your home.
              </p>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                Using our proprietary <strong style={{ color: '#033494' }}>SmartPick™ AI technology</strong>, we measure your roof with pinpoint accuracy via satellite. This eliminates the overhead costs of traditional roofing companies dayton relies on, allowing us to pass thousands of dollars in savings directly to you—without sacrificing quality.
              </p>

              <div className="flex flex-col gap-4">
                {[
                  { icon: '🛰️', title: 'Satellite Measurement', desc: 'Pinpoint-accurate roof measurements via satellite imagery—no site visit needed.' },
                  { icon: '💰', title: 'Thousands in Savings', desc: 'Lower overhead means we pass real savings directly to you.' },
                  { icon: '🏠', title: '100% Online Process', desc: 'Design, purchase, and schedule your roof from your couch.' },
                ]?.map((item) => (
                  <div key={item?.title} className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
                      style={{ backgroundColor: '#EBF8FE' }}
                    >
                      {item?.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm mb-0.5" style={{ color: '#000150' }}>{item?.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{item?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: SmartPick AI Mockup */}
            <div className="flex justify-center">
              <div
                className="relative w-full max-w-lg rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 24px 64px rgba(0,1,80,0.15)', borderRadius: '20px' }}
              >
                {/* Laptop frame mockup */}
                <div
                  className="rounded-2xl p-6"
                  style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <div className="flex-1 mx-4 h-6 rounded-full bg-white/10 flex items-center px-3">
                      <span className="text-white/50 text-xs">openbooq.com/smartpick</span>
                    </div>
                  </div>
                  {/* Screen content */}
                  <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#F8F9FA' }}>
                    <img
                      src="/assets/images/roof_top-1768329177363.png"
                      alt="SmartPick AI technology scanning a Dayton Ohio roof via satellite on a laptop screen"
                      className="w-full object-cover"
                      style={{ height: '220px' }}
                    />
                    <div className="p-4">
                      <div
                        className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: '#EBF8FE' }}
                      >
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#07B0E9' }} />
                        <span className="text-xs font-bold" style={{ color: '#07B0E9' }}>SmartPick™ AI — Scanning Roof...</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {['Roof Area', 'Pitch', 'Complexity']?.map((label, i) => (
                          <div key={label} className="bg-white rounded-lg p-2 text-center" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                            <div className="text-xs font-black" style={{ color: '#000150' }}>
                              {i === 0 ? '2,340 sq ft' : i === 1 ? '6/12' : 'Moderate'}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">{label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Badge overlay */}
                <div
                  className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: '#07B0E9', color: '#fff', boxShadow: '0 4px 12px rgba(7,176,233,0.4)' }}
                >
                  SmartPick™ AI
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ===== PREMIUM MATERIALS SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-4">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Premium Shingles, Installed by Local Experts
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              TAMKO, GAF, and Owens Corning Quality
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              Dayton weather is unpredictable. From heavy summer storms to freezing winter ice, your roof needs to endure it all. We use industry-leading, impact-resistant shingles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              {
                name: 'Titan XT',
                brand: 'TAMKO',
                tagline: 'Proven Durability',
                desc: 'TAMKO\'s best-selling architectural shingle. Exceptional dimensional look and long-lasting performance built for Ohio\'s demanding weather.',
                swatches: ['#4A4A4A', '#6B6B6B', '#8B7355', '#5C5C5C', '#3D3D3D'],
                image: '/assets/images/tamko-titan-xt-rustic-hickory-house_1_-1768340280949.jpg',
                imageAlt: 'House with TAMKO Titan XT shingles installed in Dayton Ohio',
              },
              {
                name: 'Timberline HDZ',
                brand: 'GAF',
                tagline: 'Most Popular in America',
                desc: 'America\'s #1-selling shingle. LayerLock technology and StainGuard Plus protection make it ideal for Dayton\'s climate with superior wind resistance.',
                swatches: ['#2C2C2C', '#5A4A3A', '#7A6A5A', '#4A3A2A', '#8A7A6A'],
                image: '/assets/images/gaf-timberline-hdz-charcoal-house_1_-1768332752421.jpg',
                imageAlt: 'House with GAF Timberline HDZ shingles in charcoal installed in Dayton Ohio',
                featured: true,
              },
              {
                name: 'Duration',
                brand: 'Owens Corning',
                tagline: 'SureNail Technology',
                desc: 'Owens Corning\'s flagship shingle with patented SureNail Technology for exceptional wind resistance and a beautiful, dimensional appearance.',
                swatches: ['#3A3A4A', '#5A5A6A', '#7A6A5A', '#4A4A5A', '#6A5A4A'],
                image: '/assets/images/owens-corning-oakridge-shingles-onyx-black-house_1_-1768334487021.jpg',
                imageAlt: 'House with Owens Corning Duration shingles installed in Dayton Ohio',
              },
            ]?.map((shingle) => (
              <div
                key={shingle?.name}
                className="bg-white rounded-2xl overflow-hidden flex flex-col relative"
                style={{
                  borderRadius: '12px',
                  boxShadow: shingle?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: shingle?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                {shingle?.featured && (
                  <div
                    className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ backgroundColor: '#07B0E9', color: '#fff' }}
                  >
                    Most Popular
                  </div>
                )}
                <div className="relative overflow-hidden" style={{ height: '200px' }}>
                  <img
                    src={shingle?.image}
                    alt={shingle?.imageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute bottom-0 left-0 right-0 h-16"
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.4), transparent)' }}
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="mb-3">
                    <span
                      className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full"
                      style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
                    >
                      {shingle?.brand}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-black mb-1"
                    style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}
                  >
                    {shingle?.name}
                  </h3>
                  <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{shingle?.tagline}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1">{shingle?.desc}</p>

                  {/* Color swatches */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs text-gray-400 font-medium">Colors:</span>
                    <div className="flex gap-1.5">
                      {shingle?.swatches?.map((color, i) => (
                        <div
                          key={i}
                          className="w-5 h-5 rounded-full border-2 border-white cursor-pointer hover:scale-110 transition-transform"
                          style={{ backgroundColor: color, boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Feature bullets */}
                  <div className="flex flex-col gap-1.5 mb-5">
                    {['Limited Lifetime Warranty', 'Energy Star Certified', 'Impact Resistant']?.map((feat) => (
                      <div key={feat} className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: '#07B0E9' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-xs text-gray-600 font-medium">{feat}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/shingles"
                    className="block text-center py-3 px-6 rounded-full text-white font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
                    style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 16px rgba(7,176,233,0.3)' }}
                  >
                    Select Product
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===== DAYTON WEATHER / TRUST SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
        <div className="max-w-5xl mx-auto">
          <div
            className="rounded-3xl p-10 sm:p-14 text-white text-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)',
              boxShadow: '0 24px 64px rgba(3,52,148,0.25)',
            }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
              <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="weather-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="white" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#weather-grid)" />
              </svg>
            </div>

            <div className="relative z-10">
              {/* Weather icons row */}
              <div className="flex items-center justify-center gap-8 mb-8">
                {/* Wind */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.59 4.59A2 2 0 004 12h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 12z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">High Winds</span>
                </div>
                {/* Hail */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 12z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19l-1 2m4-2l-1 2m4-2l-1 2" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Hail Storms</span>
                </div>
                {/* Snow */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18M3 12h18M5.636 5.636l12.728 12.728M18.364 5.636L5.636 18.364" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">Snow & Ice</span>
                </div>
                {/* Sun */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 opacity-90" fill="none" stroke="white" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                  </svg>
                  <span className="text-xs font-bold uppercase tracking-wider opacity-80">UV Exposure</span>
                </div>
              </div>

              <h2
                className="text-3xl sm:text-4xl font-black mb-5 leading-tight"
                style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
              >
                Built to Survive Ohio Weather.
                <br />
                <span className="opacity-90">Every Season. Every Storm.</span>
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed mb-8">
                Dayton's climate is no joke—from summer hailstorms to brutal winter ice dams, your roof takes a beating year-round. Our premium shingles from GAF, Atlas, TAMKO, and Owens Corning are engineered specifically for Ohio's extreme weather cycles, with Class 4 impact resistance and wind ratings up to 130 mph.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {[
                  { stat: '130 mph', label: 'Wind Resistance Rating' },
                  { stat: 'Class 4', label: 'Impact Resistance' },
                  { stat: 'Lifetime', label: 'Limited Warranty' },
                ]?.map((item) => (
                  <div
                    key={item?.label}
                    className="rounded-xl p-4"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}
                  >
                    <div className="text-2xl font-black mb-1">{item?.stat}</div>
                    <div className="text-xs opacity-75 font-medium uppercase tracking-wider">{item?.label}</div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  openCustomerPortal();
                }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{ backgroundColor: '#fff', color: '#033494', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
              >
                Get My Storm-Ready Roof Price
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>
      {/* ===== FINANCING SECTION ===== */}
      <section className="py-24 px-4" style={{ backgroundColor: '#F8F9FA' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
              style={{ backgroundColor: '#EBF8FE', color: '#07B0E9' }}
            >
              Flexible Payment Options
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black mb-4"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
            >
              No Hidden Fees, No Surprises.
            </h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
              Whether you choose to pay 100% upfront to lock in the lowest price, use our secure Escrow protection, or finance your new roof for as low as $491/month, you are always in control. We provide total transparency from estimation to installation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: '💵',
                title: 'Pay Upfront',
                subtitle: 'Lowest Price Guaranteed',
                desc: 'Pay 100% upfront and lock in our absolute best price. No financing fees, no interest—just the most transparent roofing deal in Dayton.',
                highlight: 'Best Price',
                highlightColor: '#22C55E',
                cta: 'Get Upfront Price',
              },
              {
                icon: '🔒',
                title: 'Escrow Protection',
                subtitle: 'Secure & Protected',
                desc: 'Your funds are held securely in escrow and only released upon your satisfaction. Complete peace of mind from day one to final inspection.',
                highlight: 'Most Secure',
                highlightColor: '#07B0E9',
                cta: 'Learn About Escrow',
                featured: true,
              },
              {
                icon: '📅',
                title: 'Monthly Financing',
                subtitle: 'From $491/month',
                desc: 'Spread the cost of your new roof over time with competitive financing rates. Get the roof you need today without breaking the bank.',
                highlight: 'Most Flexible',
                highlightColor: '#F59E0B',
                cta: 'See Financing Options',
              },
            ]?.map((option) => (
              <div
                key={option?.title}
                className="bg-white rounded-2xl p-8 flex flex-col"
                style={{
                  borderRadius: '12px',
                  boxShadow: option?.featured
                    ? '0 20px 48px rgba(7,176,233,0.15), 0 4px 16px rgba(0,1,80,0.08)'
                    : '0 4px 24px rgba(0,1,80,0.07), 0 1px 4px rgba(0,0,0,0.04)',
                  border: option?.featured ? '2px solid rgba(7,176,233,0.3)' : '1px solid rgba(0,0,0,0.04)',
                }}
              >
                <div className="text-3xl mb-4">{option?.icon}</div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-black" style={{ color: '#000150', fontFamily: "'Poppins', sans-serif" }}>
                    {option?.title}
                  </h3>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: option?.highlightColor + '20', color: option?.highlightColor }}
                  >
                    {option?.highlight}
                  </span>
                </div>
                <p className="text-xs font-semibold mb-3" style={{ color: '#07B0E9' }}>{option?.subtitle}</p>
                <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-6">{option?.desc}</p>
                <Link
                  to="/financing-options"
                  className="block text-center py-3 px-6 rounded-full font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                  style={{
                    backgroundColor: option?.featured ? '#07B0E9' : 'transparent',
                    color: option?.featured ? '#fff' : '#033494',
                    border: option?.featured ? 'none' : '2px solid #033494',
                  }}
                >
                  {option?.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===== BOTTOM CTA SECTION ===== */}
      <section
        className="py-24 px-4"
        style={{ backgroundColor: '#000150' }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
            style={{ backgroundColor: 'rgba(7,176,233,0.15)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.3)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            Start Your Roof Price
          </div>
          <h2
            className="text-3xl sm:text-4xl font-black text-white mb-5 leading-tight"
            style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.01em' }}
          >
            Ready to Get Your Exact Roof Price?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Join thousands of Dayton homeowners who've already discovered a smarter way to replace their roof. No sales calls. No pressure. Just your exact price in minutes.
          </p>

          <div className="max-w-xl mx-auto">
            <AddressInputWithMapbox
              placeholder="Enter your Dayton home address..."
              buttonText="Get My Roof Price"
              buttonId="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea-bottom"
              microcopy="Free roof price • No sales calls • Dayton & Miami Valley"
            />
            {/* Hidden Demand IQ trigger for bottom */}
            <button id="diq-journey-9674cf04-4057-4e53-acbb-533aa94307ea-bottom" className="hidden" aria-hidden="true" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
            {[
              { icon: '⚡', text: 'Roof price in 30 minutes' },
              { icon: '🚫', text: 'No sales calls ever' },
              { icon: '💯', text: 'Transparent pricing' },
              { icon: '🏆', text: 'Top-rated materials' },
            ]?.map((item) => (
              <div key={item?.text} className="flex items-center gap-2">
                <span className="text-base">{item?.icon}</span>
                <span className="text-gray-400 text-sm font-medium">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DaytonServiceAreaPage;
